/**
 * Calls Cloud Functions that proxy Google Calendar API using the executive's OAuth tokens.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  CalendarEventGroupAnchorMode,
  CalendarEventGroupSegment,
  DelegateCalendarSummary,
} from '../types';

const regionFunctions = () => getFunctions(undefined, 'us-central1');

export type ManagedExecutiveRow = {
  uid: string;
  displayName: string;
  email?: string;
  calendarConnected?: boolean;
};

export type { DelegateCalendarSummary };

export type DelegateCalendarEventRow = {
  id: string;
  calendarId: string;
  title: string;
  description?: string;
  start?: string;
  end?: string;
  location?: string;
  htmlLink?: string;
  isAllDay?: boolean;
  extendedPropertiesPrivate?: Record<string, string>;
};

export type EventGroupSegmentPayload = {
  segmentId: string;
  type: 'travel' | 'activity';
  calendarId: string;
  title: string;
  location?: string;
  fromPlaceId?: string;
  toPlaceId?: string;
  durationMinutes: number;
  travelDurationSeconds?: number;
  start: string;
  end: string;
};

export async function executiveCalendarListManagedExecutives(): Promise<{
  executives: ManagedExecutiveRow[];
}> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarListManagedExecutives')({});
  return res.data as { executives: ManagedExecutiveRow[] };
}

export async function executiveCalendarListCalendars(executiveUid: string): Promise<{
  calendars: DelegateCalendarSummary[];
}> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarListCalendars')({ executiveUid });
  return res.data as { calendars: DelegateCalendarSummary[] };
}

export async function executiveCalendarListEvents(params: {
  executiveUid: string;
  calendarIds: string[];
  timeMin: string;
  timeMax: string;
}): Promise<{ events: DelegateCalendarEventRow[] }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarListEvents')(params);
  return res.data as { events: DelegateCalendarEventRow[] };
}

export async function executiveCalendarInsertEvent(params: {
  executiveUid: string;
  calendarId: string;
  body: Record<string, unknown>;
}): Promise<{ event: Record<string, unknown> }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarInsertEvent')(params);
  return res.data as { event: Record<string, unknown> };
}

export async function executiveCalendarPatchEvent(params: {
  executiveUid: string;
  calendarId: string;
  eventId: string;
  resource: Record<string, unknown>;
}): Promise<{ event: Record<string, unknown> }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarPatchEvent')(params);
  return res.data as { event: Record<string, unknown> };
}

export async function executiveCalendarDeleteEvent(params: {
  executiveUid: string;
  calendarId: string;
  eventId: string;
}): Promise<{ success: boolean }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarDeleteEvent')(params);
  return res.data as { success: boolean };
}

export async function executiveCalendarInsertEventGroup(params: {
  executiveUid: string;
  groupId: string;
  anchorMode: CalendarEventGroupAnchorMode;
  anchorSegmentIndex: number;
  timeZone: string;
  segments: EventGroupSegmentPayload[];
  invitees?: string[];
}): Promise<{ groupId: string; segments: CalendarEventGroupSegment[] }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarInsertEventGroup')(params);
  return res.data as { groupId: string; segments: CalendarEventGroupSegment[] };
}

export async function executiveCalendarSyncEventGroup(params: {
  executiveUid: string;
  groupId: string;
  timeZone: string;
  segments: EventGroupSegmentPayload[];
}): Promise<{ groupId: string; segments: CalendarEventGroupSegment[] }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarSyncEventGroup')(params);
  return res.data as { groupId: string; segments: CalendarEventGroupSegment[] };
}

export async function executiveCalendarDeleteEventGroup(params: {
  executiveUid: string;
  groupId: string;
}): Promise<{ success: boolean }> {
  const res = await httpsCallable(regionFunctions(), 'executiveCalendarDeleteEventGroup')(params);
  return res.data as { success: boolean };
}

export function getEventGroupId(ev: DelegateCalendarEventRow): string | undefined {
  return ev.extendedPropertiesPrivate?.lctfGroupId;
}

export function isGroupedEvent(ev: DelegateCalendarEventRow): boolean {
  return Boolean(getEventGroupId(ev));
}
